import { useEffect, useState } from "react"

import { getFinancialTransactionItem } from "../../Project/api/project-finance.services.ts"

export interface InvoicePayment {
  id: string
  value: string
  status?: number
  payment_method_display?: string
  payment_date?: string | null
  lineName?: string
}

interface PaymentsState {
  loading: boolean
  payments: InvoicePayment[]
  /** At least one linked line could not be read, so the list may be short. */
  incomplete: boolean
}

interface PaymentSource {
  id: string
  lines?: { finance_line: string; finance_line_name?: string }[]
}

const IDLE: PaymentsState = { loading: false, payments: [], incomplete: false }

/**
 * The payments booked against an invoice, read from the lines it bills.
 *
 * The invoice payload carries `paid_to_date` but not the payments behind it;
 * each payment sits on a finance line and names the invoice it settles.
 */
export const useInvoicePayments = (
  invoice: PaymentSource | null
): PaymentsState => {
  const [state, setState] = useState<PaymentsState>(IDLE)
  const invoiceId = invoice?.id
  const lineKey = [
    ...new Set((invoice?.lines ?? []).map((line) => line.finance_line))
  ].join(",")

  useEffect(() => {
    const lineIds = lineKey ? lineKey.split(",") : []
    if (!invoiceId || !lineIds.length) {
      setState(IDLE)
      return
    }
    let current = true
    setState({ loading: true, payments: [], incomplete: false })

    Promise.allSettled(lineIds.map((id) => getFinancialTransactionItem(id)))
      .then((results) => {
        if (!current) return
        const payments: InvoicePayment[] = []
        let incomplete = false
        results.forEach((result) => {
          if (result.status !== "fulfilled" || !result.value?.ok) {
            incomplete = true
            return
          }
          const line = result.value.data
          ;(line?.partial_finances ?? [])
            .filter((payment) => payment.invoice === invoiceId)
            .forEach((payment) =>
              payments.push({ ...payment, lineName: line?.name })
            )
        })
        payments.sort((a, b) =>
          (a.payment_date ?? "").localeCompare(b.payment_date ?? "")
        )
        setState({ loading: false, payments, incomplete })
      })

    return () => {
      current = false
    }
  }, [invoiceId, lineKey])

  return state
}
